import { MdOutlineContentCopy } from 'react-icons/md';
import { changeDateForm, handleGoogleAnalytics } from '@/utils';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  EVENT_CALENDAR_URL,
  DESC_KEYS,
  LOGGING_TYPE,
} from '@/constants/calendar';
import { FaRegCalendarAlt, FaFileAlt, FaHome } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { BiSolidMessageDetail } from 'react-icons/bi';
import { IoPeopleSharp, IoCloseSharp } from 'react-icons/io5';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { isMobile } from 'react-device-detect';
import { useDetailFiles } from './detailFormHooks';
import {
  makeGoogleCalendarLink,
  makeiCalendarLink,
  makeOutLook365Link,
  makeOutLookLiveLink,
} from './detailFormUtils';
import ColorBox from '@/components/ui/ColorBox';
import { useCopyToClipboard } from 'usehooks-ts';
import { CalendarEvent } from '@/store/calendarStore';
import { useEffect, useRef, useState } from 'react';

const DETAIL_ITEMS = [
  { key: 'location', label: 'Location', component: FaLocationDot },
  {
    key: 'description.organization',
    label: isMobile ? 'Host' : DESC_KEYS.organization,
    component: FaHome,
  },
  {
    key: 'description.organizationContact',
    label: isMobile ? 'Host Contact' : DESC_KEYS.organizationContact,
    component: MdEmail,
  },
  {
    key: 'description.link',
    label: DESC_KEYS.link,
    component: FaExternalLinkAlt,
  },
  {
    key: 'description.speakers',
    label: DESC_KEYS.speakers,
    component: IoPeopleSharp,
  },
  {
    key: 'description.description',
    label: DESC_KEYS.description,
    component: BiSolidMessageDetail,
  },
];

const SHARE_ITEMS = [
  { label: 'Google Calendar', function: makeGoogleCalendarLink },
  { label: 'iCalendar', function: makeiCalendarLink },
  { label: 'Outlook Live', function: makeOutLookLiveLink },
  { label: 'Outlook 365', function: makeOutLook365Link },
];

const DetailForm = ({
  event,
  hideCloseButton,
}: {
  event: CalendarEvent;
  hideCloseButton?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setHovered] = useState(false);
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [copiedText, copy] = useCopyToClipboard();
  const isFirstRender = useRef(true);

  const { flyer, document } = useDetailFiles(id); // flyer, document url 가져오기

  const formattedDate = changeDateForm(event.start, event.end);

  useEffect(() => {
    if (isFirstRender.current) {
      handleGoogleAnalytics({
        eventType: LOGGING_TYPE.eventDetail,
        eventLabel: event.summary,
      });
      isFirstRender.current = false;
    }
  }, []);

  const CloseBtnClick = () => {
    if (location.state) navigate(-1);
    else navigate(EVENT_CALENDAR_URL);
  };

  const handleClickCopyLink = () => {
    // copy current url
    copy(window.location.href).then(() => {
      alert('url copied!');
    });
  };

  const handleClickShareButton = item => {
    handleGoogleAnalytics({
      eventType: LOGGING_TYPE.eventShare,
      eventLabel: event.summary,
    });
    handleiCalendar(item.label);
  };

  const handleiCalendar = label => {
    if (label === 'iCalendar')
      alert('Open Downloaded File to Add to iCalendar');
  };

  const handleFileDownload = () => {
    handleGoogleAnalytics({
      eventType: LOGGING_TYPE.documentDownload,
      eventLabel: event.summary,
    });
  };

  return (
    <section className='w-full relative h-screen flex items-start pb-8 z-[2] overflow-y-auto overflow-x-hidden'>
      <div
        className={`w-full md:w-[80%] pt-8 md:pt-16 mx-auto relative flex flex-col gap-4`}
      >
        <section className='px-0 flex flex-col gap-4'>
          <div className='flex flex-row justify-between items-center min-w-0'>
            {!hideCloseButton && (
              <IoCloseSharp
                cursor={'pointer'}
                size={'2rem'}
                onClick={CloseBtnClick}
              />
            )}
            <button
              type='button'
              className='relative top-0 right-0 py-2 px-3 rounded-md flex flex-col items-center gap-2 !text-sm md:!text-base font-bold bg-[#001326] text-white cursor-pointer border-0 break-words'
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              Add to my calendar
              {isHovered && (
                <div className='absolute top-full w-full rounded-md shadow-[5px_5px_10px_rgba(0,0,0,0.63)] bg-white'>
                  {SHARE_ITEMS.map((item, idx) => {
                    return (
                      <a
                        key={idx}
                        href={item.function(event)}
                        target='_blank'
                        rel='noreferrer'
                        download={`${event.summary}.ics`}
                        onClick={() => handleClickShareButton(item)}
                        className='block py-2 px-3 box-border !text-sm md:!text-base text-[#001326] no-underline'
                      >
                        {item.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </button>
          </div>
          <h2
            className='m-0 p-0 mb-6 flex items-center gap-2 w-full break-words cursor-pointer !text-base md:!text-lg'
            onClick={handleClickCopyLink}
          >
            <ColorBox color={event.backgroundColor} />
            {event.summary}
            <MdOutlineContentCopy />
          </h2>
          <div className='flex items-start min-w-0'>
            <div className='flex items-center flex-shrink-0'>
              <FaRegCalendarAlt className='w-5 h-5' />
              <span
                className={`ml-2 !text-sm md:!text-base ${isMobile ? 'w-40' : 'w-60'}`}
              >
                Schedule
              </span>
            </div>
            <div className='w-full whitespace-pre-wrap !text-sm md:!text-base break-words'>
              {formattedDate}
            </div>
          </div>
          {DETAIL_ITEMS.map((item, index) => {
            const IconComponent = item.component;
            const value = getNestedValue(event, item.key);
            const isSpeakers = item.key === 'description.speakers';
            if (!isSpeakers)
              return (
                value && (
                  <div key={index} className='flex items-start min-w-0'>
                    <div className='flex items-center flex-shrink-0'>
                      <IconComponent className='w-5 h-5' />
                      <span
                        className={`ml-2 !text-sm md:!text-base ${isMobile ? 'w-40' : 'w-60'}`}
                      >
                        {item.label}
                      </span>
                    </div>
                    <div className='w-full whitespace-pre-wrap !text-sm md:!text-base break-words min-w-0'>
                      {item.key === 'description.link' ? (
                        <a
                          href={value}
                          target='_blank'
                          rel='noreferrer'
                          className='inline-block max-w-full whitespace-pre-wrap break-words'
                        >
                          {value}
                        </a>
                      ) : (
                        value
                      )}
                    </div>
                  </div>
                )
              );

            return (
              value &&
              value.length > 0 && (
                <div key={index} className='flex items-start min-w-0'>
                  <div className='flex items-center flex-shrink-0'>
                    <IconComponent className='w-5 h-5' />
                    <span
                      className={`ml-2 !text-sm md:!text-base ${isMobile ? 'w-40' : 'w-60'}`}
                    >
                      {item.label}
                    </span>
                  </div>
                  <div className='flex flex-col justify-start gap-2'>
                    {value.map((speaker, index) => (
                      <div
                        key={index}
                        className='w-full whitespace-pre-wrap !text-sm md:!text-base break-words'
                      >
                        {speaker}
                      </div>
                    ))}
                  </div>
                </div>
              )
            );
          })}
          {document && (
            <div className='flex items-start min-w-0'>
              <div className='flex items-center flex-shrink-0'>
                <FaFileAlt className='w-5 h-5' />
                <span
                  className={`ml-2 !text-sm md:!text-base ${isMobile ? 'w-40' : 'w-60'}`}
                >
                  Document
                </span>
              </div>
              <a
                href={document}
                target='_blank'
                rel='noreferrer'
                onClick={handleFileDownload}
                className='w-full whitespace-pre-wrap'
              >
                <div className='w-full whitespace-pre-wrap !text-sm md:!text-base'>
                  download
                </div>
              </a>
            </div>
          )}
        </section>
        {flyer && (
          <div className='relative w-full h-full bg-[#e9e9e9] rounded-3xl flex justify-center items-center cursor-pointer'>
            <img
              src={flyer}
              alt=''
              className='w-full h-full object-cover object-center'
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default DetailForm;

function getNestedValue(obj: any, key: string) {
  return key.split('.').reduce((acc, part) => acc && acc[part], obj);
}
